import React, {useCallback} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import IssueFieldInlineEdit from '../forms/IssueFieldInlineEdit';
import OwnerInlineEdit from '../forms/OwnerInlineEdit';
import CustomRootCauseInlineEdit from '../forms/CustomRootCauseInlineEdit';
import IssueSubtypeInlineEdit from '../forms/IssueSubtypeInlineEdit';
import AssignedToInlineEdit from '../forms/AssignedToInlineEdit';
import DueDateToInlineEdit from '../forms/DueDateToInlineEdit';
import LbsLocationInlineEdit from '../forms/LbsLocationInlineEdit';
import LocationDescriptionInlineEdit from '../forms/LocationDescriptionInlineEdit';
import LinkedDocumentInlineEditContainer from '../forms/LinkedDocumentInlineEditContainer';
import {issueAttributesType, locationsTreeNodesType} from '../../constants/proptype-validation';
import {viewTypes} from '../../constants/view-types';
import {ISSUE_ATTRIBUTE_NAMES} from '../../constants/issue-attribute-names';
import {ISSUES_PREVIEW_FIELD_NAMES, PANELS} from '../../constants/issuePreview';
import LocationsThunk from '../../app/thunk/locationsThunk';
import {
  getActiveFieldIssueSubTypesByParentId,
  getActiveIssueRootCausesGroupedDropdownOptions,
  getActiveIssueSubTypesGroupedDropdownOptions,
  getActiveUsers,
  getCurrentDocument,
  getCurrentViewType,
  getDocumentCollection,
  getIsLocationsLoadingError,
  getIsLocationsTreeLoading,
  getIssueAttributesMappingEntitiesMap,
  getIssueRootCausesDefinitionsEntitiesMap,
  getIssueTypesAdminEntitiesMap,
  getLocationsTreeNodes,
  getRequiredFieldsFormatted,
  getEntityUserNameGetter,
  isFieldManagementActive
} from '../../app/selectors';
import {connect} from '../../app/connect';
import ChecklistCreatedFromContainer from '../common/ChecklistCreatedFromContainer';
import featureFlags, {FEATURE_FLAGS} from '../../utils/featureFlags';

const dropdownMaxWidth = 275;

const IssuePreviewDetailsTabGeneralSection = ({
  issue,
  viewType,
  isLocationsTreeLoading,
  isLocationsLoadingError,
  locationsTreeNodes,
  users,
  fieldIssueTypeEntitiesMap,
  activeIssueSubTypesGroupedDropdownOptions,
  fieldIssueSubTypes,
  container,
  activeIssueRootCausesGroupedDropdownOptions,
  rootCauseEntitiesMap,
  issueCustomAttributeMappings,
  requiredFieldsMap,
  dispatch,
  getEntityDisplayNameById,
  contentElementRef,
  showChecklistIndicator
}) => {
  const getDefaultLocationTree = useCallback(() => dispatch(LocationsThunk.getDefaultTree()), [dispatch]);

  const renderAttribute = useCallback(attributeName => {
    switch (attributeName) {
      case ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID: {
        return (
          <IssueSubtypeInlineEdit
            key={ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID}
            issue={issue}
            dropdownMaxWidth={dropdownMaxWidth}
            fieldIssueTypeEntitiesMap={fieldIssueTypeEntitiesMap}
            fieldIssueSubTypes={fieldIssueSubTypes}
            activeIssueSubTypesGroupedDropdownOptions={activeIssueSubTypesGroupedDropdownOptions}
            className="IssuePreviewDetailsTabGeneralSection__field"
            styleType={IssueFieldInlineEdit.StyleTypes.SMALL}
            issueCustomAttributeMappings={issueCustomAttributeMappings}
            container={container}
          />
        );
      }
      case ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO: {
        return (
          <AssignedToInlineEdit
            key={ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO}
            issue={issue}
            className="IssuePreviewDetailsTabGeneralSection__field"
            styleType={IssueFieldInlineEdit.StyleTypes.SMALL}
            isRequired={requiredFieldsMap[ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO]}
          />
        );
      }
      case ISSUE_ATTRIBUTE_NAMES.DUE_DATE:
        return (
          <DueDateToInlineEdit
            key={ISSUE_ATTRIBUTE_NAMES.DUE_DATE}
            issue={issue}
            className="IssuePreviewDetailsTabGeneralSection__field"
            styleType={IssueFieldInlineEdit.StyleTypes.SMALL}
            isRequired={requiredFieldsMap[ISSUE_ATTRIBUTE_NAMES.DUE_DATE]}
          />
        );
      case ISSUE_ATTRIBUTE_NAMES.LOCATION_ID: {
        return (
          <LbsLocationInlineEdit
            key={ISSUE_ATTRIBUTE_NAMES.LOCATION_ID}
            issue={issue}
            isLocationsTreeLoading={isLocationsTreeLoading}
            isLocationsLoadingError={isLocationsLoadingError}
            getDefaultLocationTree={getDefaultLocationTree}
            locationsTreeNodes={locationsTreeNodes}
            className="IssuePreviewDetailsTabGeneralSection__field"
            data-testid="LocationInlineEdit"
            styleType={IssueFieldInlineEdit.StyleTypes.SMALL}
            isRequired={requiredFieldsMap[ISSUE_ATTRIBUTE_NAMES.LOCATION_ID]}
            container={contentElementRef}
          />
        );
      }
      case ISSUE_ATTRIBUTE_NAMES.LOCATION_DETAILS:
        return (
          <LocationDescriptionInlineEdit
            key={ISSUE_ATTRIBUTE_NAMES.LOCATION_DETAILS}
            issue={issue}
            className="IssuePreviewDetailsTabGeneralSection__field"
            styleType={IssueFieldInlineEdit.StyleTypes.SMALL}
            isRequired={requiredFieldsMap[ISSUE_ATTRIBUTE_NAMES.LOCATION_DETAILS]}
            data-testid="LocationDescriptionInlineEdit"
          />
        );
      case ISSUE_ATTRIBUTE_NAMES.OWNER_ID: {
        return (
          <OwnerInlineEdit
            key={ISSUE_ATTRIBUTE_NAMES.OWNER_ID}
            issue={issue}
            users={users}
            getEntityDisplayNameById={getEntityDisplayNameById}
            className="IssuePreviewDetailsTabGeneralSection__field"
            styleType={IssueFieldInlineEdit.StyleTypes.SMALL}
          />
        );
      }
      case ISSUE_ATTRIBUTE_NAMES.ROOT_CAUSE_ID: {
        return (
          <CustomRootCauseInlineEdit
            key={ISSUE_ATTRIBUTE_NAMES.ROOT_CAUSE_ID}
            issue={issue}
            activeIssueRootCausesGroupedDropdownOptions={activeIssueRootCausesGroupedDropdownOptions}
            rootCauseEntitiesMap={rootCauseEntitiesMap}
            className="IssuePreviewDetailsTabGeneralSection__field IssuePreviewDetailsTabGeneralSection__custom-root-cause"
            styleType={IssueFieldInlineEdit.StyleTypes.SMALL}
            isRequired={requiredFieldsMap[ISSUE_ATTRIBUTE_NAMES.ROOT_CAUSE_ID]}
            container={contentElementRef}
          />
        );
      }
      case ISSUE_ATTRIBUTE_NAMES.LINKED_DOCUMENTS: {
        return (
          <LinkedDocumentInlineEditContainer
            key={ISSUE_ATTRIBUTE_NAMES.LINKED_DOCUMENTS}
            issue={issue}
            container={container}
            contentElementRef={contentElementRef}
            className="IssuePreviewDetailsTabGeneralSection__field"
          />
        );
      }
      case ISSUE_ATTRIBUTE_NAMES.LINKS: {
        return showChecklistIndicator && <ChecklistCreatedFromContainer container={contentElementRef} />;
      }

      default:
        return null;
    }
  }, [
    activeIssueRootCausesGroupedDropdownOptions,
    activeIssueSubTypesGroupedDropdownOptions,
    container,
    fieldIssueSubTypes,
    fieldIssueTypeEntitiesMap,
    getDefaultLocationTree,
    isLocationsLoadingError,
    isLocationsTreeLoading,
    issue,
    issueCustomAttributeMappings,
    locationsTreeNodes,
    requiredFieldsMap,
    rootCauseEntitiesMap,
    users,
    getEntityDisplayNameById,
    contentElementRef,
    showChecklistIndicator
  ]);

  const issuePreviewAttributeNames = ISSUES_PREVIEW_FIELD_NAMES[viewType];
  const divClassNames = classNames('IssuePreviewDetailsTabGeneralSection', {
    'IssuePreviewDetailsTabGeneralSection--narrow': viewType === viewTypes.DOCUMENT,
    'IssuePreviewDetailsTabGeneralSection--wide': viewType === viewTypes.PROJECT
  });

  return (
    <form className={divClassNames} onSubmit={e => e.preventDefault()}>
      <div className="IssuePreviewDetailsTabGeneralSection__first-panel">
        {issuePreviewAttributeNames[PANELS.FIRST_PANEL].map(renderAttribute)}
      </div>
      <div className="IssuePreviewDetailsTabGeneralSection__second-panel">
        {issuePreviewAttributeNames[PANELS.SECOND_PANEL].map(renderAttribute)}
      </div>
    </form>
  );
};

IssuePreviewDetailsTabGeneralSection.displayName = 'IssuePreviewDetailsTabGeneralSection';

IssuePreviewDetailsTabGeneralSection.propTypes = {
  issue: issueAttributesType.isRequired,
  viewType: PropTypes.string.isRequired,
  isLocationsTreeLoading: PropTypes.bool,
  isLocationsLoadingError: PropTypes.bool,
  dispatch: PropTypes.func.isRequired,
  locationsTreeNodes: locationsTreeNodesType,
  users: PropTypes.array.isRequired,
  fieldIssueTypeEntitiesMap: PropTypes.object.isRequired,
  activeIssueSubTypesGroupedDropdownOptions: PropTypes.array.isRequired,
  fieldIssueSubTypes: PropTypes.array,
  container: PropTypes.object.isRequired,
  activeIssueRootCausesGroupedDropdownOptions: PropTypes.array.isRequired,
  rootCauseEntitiesMap: PropTypes.object.isRequired,
  issueCustomAttributeMappings: PropTypes.object.isRequired,
  requiredFieldsMap: PropTypes.object.isRequired,
  getEntityDisplayNameById: PropTypes.func.isRequired,
  contentElementRef: PropTypes.object,
  showChecklistIndicator: PropTypes.bool.isRequired
};

const mapStateToProps = (state, ownProps) => {
  const issueTypeId = ownProps.issue[ISSUE_ATTRIBUTE_NAMES.ISSUE_TYPE_ID];

  return {
    document: getCurrentDocument(state),
    isLocationsLoadingError: getIsLocationsLoadingError(state),
    isLocationsTreeLoading: getIsLocationsTreeLoading(state),
    locationsTreeNodes: getLocationsTreeNodes(state),
    users: getActiveUsers(state),
    documentCollection: getDocumentCollection(state),
    fieldIssueSubTypes: getActiveFieldIssueSubTypesByParentId(state, issueTypeId),
    activeIssueSubTypesGroupedDropdownOptions: getActiveIssueSubTypesGroupedDropdownOptions(state),
    fieldIssueTypeEntitiesMap: getIssueTypesAdminEntitiesMap(state),
    activeIssueRootCausesGroupedDropdownOptions: getActiveIssueRootCausesGroupedDropdownOptions(state),
    rootCauseEntitiesMap: getIssueRootCausesDefinitionsEntitiesMap(state),
    viewType: getCurrentViewType(state),
    issueCustomAttributeMappings: getIssueAttributesMappingEntitiesMap(state),
    requiredFieldsMap: getRequiredFieldsFormatted(state),
    getEntityDisplayNameById: getEntityUserNameGetter(state),
    showChecklistIndicator: isFieldManagementActive(state) && featureFlags.get(FEATURE_FLAGS.CHECKLIST_INDICATOR)
  };
};

export default connect(mapStateToProps)(IssuePreviewDetailsTabGeneralSection);
